import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { HelpCircle, Search, LifeBuoy, MessageCircle } from 'lucide-react';
import { API } from '../utils/api';

const TICKET_CATEGORIES = ['account', 'payment', 'sub_account', 'managed_user', 'privacy', 'bug', 'feature_request', 'other'];

const STATUS_COLORS = {
  new: 'bg-blue-50 text-blue-700 border-blue-200',
  open: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-green-50 text-green-700 border-green-200',
  closed: 'bg-gray-100 text-gray-500 border-gray-200',
};

export function HelpFaqSection({ headers }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'sv' ? 'sv' : 'en';
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [activeCat, setActiveCat] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticket, setTicket] = useState({ category: 'other', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [myTickets, setMyTickets] = useState([]);
  const [ticketDetail, setTicketDetail] = useState(null);

  useEffect(() => {
    let ignore = false;
    axios.get(`${API}/faq/categories?language=${lang}`, { headers })
      .then(r => { if (!ignore) setCategories(r.data.categories || []); })
      .catch(() => {});
    fetchTickets();
    return () => { ignore = true; };
  }, [lang]);

  const fetchTickets = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/support/tickets?limit=5`, { headers });
      setMyTickets(r.data.tickets || []);
    } catch (e) { /* silent */ }
  }, [headers]);

  useEffect(() => {
    let ignore = false;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ language: lang });
        if (activeCat !== 'all') params.set('category', activeCat);
        if (q.trim()) params.set('q', q.trim());
        const r = await axios.get(`${API}/faq?${params}`, { headers });
        if (!ignore) setItems(r.data.items || []);
      } catch (e) {
        console.error('FAQ fetch failed:', e);
      } finally {
        if (!ignore) setLoading(false);
      }
    }, q ? 400 : 0);
    return () => { ignore = true; clearTimeout(handle); };
  }, [q, activeCat, lang]);

  const sendTicket = async () => {
    if (!ticket.subject.trim() || !ticket.message.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/support/tickets`,
        { ...ticket, platform: 'web' }, { headers });
      toast.success(t('helpCenter.ticketSent'));
      setTicketOpen(false);
      setTicket({ category: 'other', subject: '', message: '' });
      fetchTickets();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('helpCenter.ticketFailed'));
    } finally {
      setSending(false);
    }
  };

  const openTicketDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/support/tickets/${id}`, { headers });
      setTicketDetail(r.data);
    } catch (e) { /* silent */ }
  };

  return (
    <div className="space-y-4" data-testid="help-faq-section">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <HelpCircle className="w-5 h-5 text-teal-600" /> {t('helpCenter.title')}
      </h2>

      <Card data-testid="faq-card">
        <CardContent className="pt-4 space-y-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input className="pl-9" placeholder={t('helpCenter.searchPlaceholder')}
              value={q} onChange={e => setQ(e.target.value)} data-testid="faq-search-input" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setActiveCat('all')}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${activeCat === 'all' ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}
              data-testid="faq-cat-all">
              {t('helpCenter.all')}
            </button>
            {categories.map(c => (
              <button key={c.id} onClick={() => setActiveCat(c.slug)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${activeCat === c.slug ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}
                data-testid={`faq-cat-${c.slug}`}>
                {c.title} ({c.item_count})
              </button>
            ))}
          </div>

          {loading ? (
            <div className="animate-pulse bg-gray-100 h-24 rounded" data-testid="faq-loading" />
          ) : items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4" data-testid="faq-empty">{t('helpCenter.noResults')}</p>
          ) : (
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {items.map(it => (
                <AccordionItem key={it.id} value={it.id}>
                  <AccordionTrigger className="text-sm text-left" data-testid={`faq-q-${it.id}`}>{it.question}</AccordionTrigger>
                  <AccordionContent className="text-sm text-gray-600 whitespace-pre-line">{it.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
      </Card>

      <Card data-testid="contact-support-card">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-teal-600" /> {t('helpCenter.contactTitle')}
          </CardTitle>
          <CardDescription>{t('helpCenter.contactDesc')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button size="sm" onClick={() => setTicketOpen(true)} data-testid="new-ticket-btn">
            <MessageCircle className="w-4 h-4 mr-1" /> {t('helpCenter.newTicket')}
          </Button>
          {myTickets.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-gray-500">{t('helpCenter.myTickets')}</Label>
              {myTickets.map(tk => (
                <button key={tk.id} onClick={() => openTicketDetail(tk.id)}
                  className="w-full flex items-center justify-between p-2 border rounded-lg text-left hover:bg-gray-50"
                  data-testid={`ticket-row-${tk.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{tk.subject}</div>
                    <div className="text-xs text-gray-400">
                      {t(`helpCenter.categories.${tk.category}`)} · {new Date(tk.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Badge variant="outline" className={`shrink-0 ${STATUS_COLORS[tk.status] || ''}`}>
                    {t(`helpCenter.status.${tk.status}`, tk.status)}
                  </Badge>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New ticket dialog */}
      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="max-w-md" data-testid="new-ticket-dialog">
          <DialogHeader>
            <DialogTitle>{t('helpCenter.newTicket')}</DialogTitle>
            <DialogDescription>{t('helpCenter.contactDesc')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('helpCenter.category')}</Label>
              <Select value={ticket.category} onValueChange={v => setTicket({ ...ticket, category: v })}>
                <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TICKET_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{t(`helpCenter.categories.${c}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('helpCenter.subject')}</Label>
              <Input maxLength={200} value={ticket.subject}
                onChange={e => setTicket({ ...ticket, subject: e.target.value })} data-testid="ticket-subject-input" />
            </div>
            <div>
              <Label>{t('helpCenter.message')}</Label>
              <Textarea rows={4} maxLength={4000} value={ticket.message}
                onChange={e => setTicket({ ...ticket, message: e.target.value })} data-testid="ticket-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketOpen(false)}>{t('helpCenter.cancel')}</Button>
            <Button onClick={sendTicket} disabled={sending || !ticket.subject.trim() || !ticket.message.trim()}
              data-testid="ticket-send-btn">
              {t('helpCenter.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket detail dialog */}
      <Dialog open={!!ticketDetail} onOpenChange={(o) => !o && setTicketDetail(null)}>
        <DialogContent className="max-w-md" data-testid="ticket-detail-dialog">
          <DialogHeader>
            <DialogTitle>{ticketDetail?.subject}</DialogTitle>
            <DialogDescription>
              {ticketDetail && `${t(`helpCenter.categories.${ticketDetail.category}`)} · ${new Date(ticketDetail.created_at).toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="whitespace-pre-line text-gray-700">{ticketDetail?.message}</p>
            {ticketDetail?.admin_response && (
              <div className="p-3 rounded-lg bg-teal-50 border border-teal-200">
                <div className="text-xs font-medium text-teal-700 mb-1">{t('helpCenter.response')}</div>
                <p className="whitespace-pre-line text-gray-700">{ticketDetail.admin_response}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
